import React, { useState } from 'react';
import { useAsync } from 'react-use';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import {
  EmptyState,
  InfoCard,
  Progress,
  ResponseErrorPanel,
} from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';
import { Checkbox, Divider, FormControlLabel } from '@material-ui/core';
import { RepoSection } from '../PRList';
import { RepoResult } from '../../types';

export function EntitySystemPRCard() {
  const { entity } = useEntity();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [showDependabot, setShowDependabot] = useState(false);

  const reposAnnotation =
    entity.metadata.annotations?.['github-pr-dashboard/repos'];
  const repos = reposAnnotation
    ? reposAnnotation.split(',').map(r => r.trim()).filter(Boolean)
    : [];

  const { value, loading, error } = useAsync(async (): Promise<RepoResult[]> => {
    if (repos.length === 0) return [];
    const baseUrl = await discoveryApi.getBaseUrl('github-pr-dashboard');
    const response = await fetchApi.fetch(
      `${baseUrl}/prs?repos=${encodeURIComponent(repos.join(','))}`,
    );
    if (!response.ok) {
      throw new Error(`Failed to fetch PRs: ${response.statusText}`);
    }
    return response.json();
  }, [reposAnnotation]);

  if (repos.length === 0) {
    return (
      <InfoCard title="Pull Requests">
        <EmptyState
          missing="field"
          title="Missing annotation"
          description="Add the 'github-pr-dashboard/repos' annotation to this system's catalog-info.yaml with a comma-separated list of owner/repo slugs to enable the PR dashboard."
        />
      </InfoCard>
    );
  }

  if (loading) {
    return <Progress />;
  }

  if (error) {
    return <ResponseErrorPanel error={error} />;
  }

  const results: RepoResult[] = (value ?? []).map(result => ({
    ...result,
    pullRequests: showDependabot
      ? result.pullRequests
      : result.pullRequests.filter(pr => pr.author !== 'dependabot[bot]'),
  }));

  const action = (
    <FormControlLabel
      control={
        <Checkbox
          size="small"
          checked={showDependabot}
          onChange={e => setShowDependabot(e.target.checked)}
        />
      }
      label="Show Dependabot"
    />
  );

  return (
    <InfoCard title="Pull Requests" action={action}>
      {results.map((result, idx) => (
        <React.Fragment key={result.repo}>
          {idx > 0 && <Divider />}
          <RepoSection result={result} />
        </React.Fragment>
      ))}
    </InfoCard>
  );
}
